import React, { useCallback, useEffect, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { Moon, RotateCcw, Sparkles } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STORAGE_KEY = "maqam_salah_v1";
const PRAYERS = ["Fajr", "Dhuhr", "Asr", "Maghrib", "Isha"];
const LETTERS = { Fajr: "F", Dhuhr: "D", Asr: "A", Maghrib: "M", Isha: "I" };
const MADINAH_DAYS = ["2026-10-11", "2026-10-12", "2026-10-13", "2026-10-14", "2026-10-15"];
const MAKKAH_DAYS = ["2026-10-17", "2026-10-18", "2026-10-19", "2026-10-20", "2026-10-21"];
const TRAVEL_DAY = "2026-10-16";

const Gold = ({ children }) => <span className="font-bold text-[#B8860B]">{children}</span>;

const dayLabel = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-GB", { month: "short", day: "numeric" });
};

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export const SalahTracker = () => {
  const { t, lang } = useLang();
  const [state, setState] = useState(load);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const toggle = useCallback((date, prayer) => {
    setState((s) => {
      const day = { ...(s[date] || {}) };
      day[prayer] = !day[prayer];
      return { ...s, [date]: day };
    });
  }, []);

  const setTravel = useCallback((prayer, city) => {
    setState((s) => {
      const day = { ...(s[TRAVEL_DAY] || {}) };
      day[prayer] = day[prayer] === city ? null : city;
      return { ...s, [TRAVEL_DAY]: day };
    });
  }, []);

  const reset = useCallback(() => setState({}), []);

  const cityCount = (days) =>
    days.reduce((acc, d) => acc + PRAYERS.filter((p) => state[d]?.[p]).length, 0);
  const madinahDone = cityCount(MADINAH_DAYS);
  const makkahDone = cityCount(MAKKAH_DAYS);
  const travelDone = PRAYERS.filter((p) => state[TRAVEL_DAY]?.[p]).length;

  const Summary = ({ label, done, total }) => (
    <div className="mb-3">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-bold text-[#0A192F]">{label}</span>
        <span className="text-xs font-bold text-[#3E6B9A]">{done} {t("salah.of")} {total}</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-[#0A192F] transition-all duration-500" style={{ width: `${(done / total) * 100}%` }} />
      </div>
    </div>
  );

  const DayRow = ({ date }) => (
    <div className="flex items-center gap-2" data-testid={`salah-day-${date}`}>
      <span className="w-12 shrink-0 text-xs font-semibold text-slate-500">{dayLabel(date)}</span>
      <div className="flex flex-1 gap-1.5">
        {PRAYERS.map((p) => {
          const on = !!state[date]?.[p];
          return (
            <button
              key={p}
              onClick={() => toggle(date, p)}
              data-testid={`salah-${date}-${p}`}
              aria-pressed={on}
              className={`flex h-9 flex-1 items-center justify-center rounded-lg text-xs font-bold transition-all active:scale-95 ${
                on ? "bg-[#0A192F] text-[#E9CE7D]" : "bg-slate-100 text-slate-400 hover:bg-slate-200"
              }`}
            >
              {LETTERS[p]}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="maqam-card overflow-hidden p-0" data-testid="salah-tracker">
      <div className="flex items-center gap-2.5 border-b border-slate-100 px-5 py-4">
        <span className="grid h-9 w-9 place-items-center rounded-xl bg-[#0A192F] text-[#E9CE7D]">
          <Moon size={17} strokeWidth={1.8} />
        </span>
        <div>
          <h3 className="font-display text-base font-bold leading-tight text-[#0A192F]">{t("salah.title")}</h3>
          <p className="text-[11px] text-slate-400">{t("salah.subtitle")}</p>
        </div>
      </div>

      <div className="p-5">
        {/* Madinah */}
        <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-[#D4AF37]">{t("salah.madinah")}</p>
        <Summary label={t("salah.madinah")} done={madinahDone} total={25} />
        <div className="mb-5 space-y-1.5">{MADINAH_DAYS.map((d) => <DayRow key={d} date={d} />)}</div>

        {/* Travel day */}
        <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[#D4AF37]">{t("salah.travelday")}</p>
        <p className="mb-2.5 text-[11px] text-slate-400">{t("salah.travelhint")}</p>
        <div className="mb-5 space-y-2" data-testid="salah-travel">
          {PRAYERS.map((p) => {
            const v = state[TRAVEL_DAY]?.[p];
            return (
              <div key={p} className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-[#0A192F]">{t(`prayer.${p.toLowerCase()}`)}</span>
                <div className="flex gap-1.5">
                  {["Madinah", "Makkah"].map((c) => (
                    <button
                      key={c}
                      onClick={() => setTravel(p, c)}
                      data-testid={`salah-travel-${p}-${c}`}
                      className={`rounded-full px-3 py-1 text-[11px] font-bold transition-all active:scale-95 ${
                        v === c ? "bg-[#0A192F] text-[#E9CE7D]" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                      }`}
                    >
                      {c === "Makkah" ? t("prayer.makkah") : t("prayer.madinah")}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          <p className="pt-0.5 text-right text-[11px] font-semibold text-[#3E6B9A]">{travelDone} {t("salah.of")} 5 {t("salah.done")}</p>
        </div>

        {/* Makkah */}
        <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-[#D4AF37]">{t("salah.makkah")}</p>
        <Summary label={t("salah.makkah")} done={makkahDone} total={25} />
        <div className="mb-4 space-y-1.5">{MAKKAH_DAYS.map((d) => <DayRow key={d} date={d} />)}</div>

        <div className="rounded-2xl bg-[#EAF2FC] p-3.5">
          <p className="flex items-start gap-2 text-xs leading-relaxed text-[#0A1733]">
            <Sparkles size={14} className="mt-0.5 shrink-0 text-[#D4AF37]" />
            <span data-testid="salah-note">
              {lang === "fr" ? (
                <>Une prière à Masjid an-Nabawi vaut mieux que <Gold>1 000</Gold> prières ailleurs, et une prière à Masjid al-Haram (La Mecque) vaut mieux que <Gold>100 000</Gold> prières ailleurs.</>
              ) : (
                <>A prayer in Masjid an-Nabawi is better than <Gold>1,000</Gold> prayers elsewhere, and a prayer in Masjid al-Haram (Makkah) is better than <Gold>100,000</Gold> prayers elsewhere.</>
              )}
            </span>
          </p>
          <p className="mt-1.5 pl-6 text-[11px] font-medium text-[#475569]" data-testid="salah-note-cite">
            {t("salah.noteCite")}
          </p>
        </div>

        <div className="mt-4 flex justify-center">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                data-testid="salah-reset-btn"
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-700 active:scale-95"
              >
                <RotateCcw size={14} /> {t("salah.reset")}
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="salah-reset-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>{t("salah.resetTitle")}</AlertDialogTitle>
                <AlertDialogDescription>{t("salah.resetBody")}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("guide.cancel")}</AlertDialogCancel>
                <AlertDialogAction data-testid="salah-reset-confirm" onClick={reset} className="bg-[#B42318] hover:bg-[#9a1e14]">
                  {t("salah.reset")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
};
